import { Button, Flex, Text } from '@servicetitan/anvil2';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { formatCurrency } from '@servicetitan/financing-utils-shared';
import { TextField } from '@servicetitan/anvil2-react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePrequalsFinancingInformationSchema } from './schema';
import { useState, useEffect } from 'react';
import { PrequalsStore } from './prequalsStore';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';
import { GoodLeapForm } from './goodLeap/Form';

export interface PrequalsFormProps {
    onSubmit: SubmitHandler<{ amount: number; state?: string }>;
    defaultAmount: number;
    prequalAmount: number;
    defaultState?: string;
    availableStates: string[];
    isGoodLeap: boolean;
}

export const Form = ({
    onSubmit,
    defaultAmount,
    prequalAmount,
    defaultState,
    availableStates,
    isGoodLeap,
}: PrequalsFormProps) => {
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);
    const { setIsAmountConfirmed } = PrequalsStore.useStore();

    const { minAmount } = useLoanAmountValidation(defaultAmount);

    const schema = usePrequalsFinancingInformationSchema(
        prequalAmount,
        minAmount,
        isGoodLeap,
        availableStates,
        isGoodLeap, // Skip max amount validation for GoodLeap
    );

    const getInitialState = (): string | undefined => {
        if (!isGoodLeap) {
            return undefined;
        }

        if (availableStates.length === 1) {
            return availableStates[0];
        }

        if (availableStates.length > 1 && defaultState && availableStates.includes(defaultState)) {
            return defaultState;
        }

        return undefined;
    };

    const methods = useForm({
        defaultValues: {
            amount: defaultAmount,
            ...(isGoodLeap ? { state: getInitialState() } : {}),
        },
        resolver: zodResolver(schema),
        mode: 'onBlur',
    });

    // Update amount when defaultAmount changes (e.g., when rule defaultLoanAmount loads)
    useEffect(() => {
        if (defaultAmount !== methods.getValues('amount')) {
            methods.setValue('amount', defaultAmount, { shouldDirty: false });
        }
    }, [defaultAmount, methods]);

    // Update resolver when schema changes (e.g., when rule states are loaded)
    useEffect(() => {
        methods.clearErrors();
        // Trigger re-validation if form has been touched
        if (methods.formState.isDirty) {
            methods.trigger();
        }
    }, [schema, methods]);

    const resetAmount = () => {
        // For GoodLeap, reset to defaultAmount (from rule or saleAmount), otherwise use prequalAmount
        const resetAmountValue = isGoodLeap ? defaultAmount : prequalAmount;
        const resetValues: { amount: number; state?: string } = { amount: resetAmountValue };

        if (isGoodLeap) {
            if (availableStates.length === 1) {
                resetValues.state = availableStates[0];
            } else if (defaultState && availableStates.includes(defaultState)) {
                resetValues.state = defaultState;
            }
        }

        methods.reset(resetValues, { keepValues: false });
        setHasBeenSubmitted(false);
        setIsAmountConfirmed(false);
    };

    const handleSubmit: SubmitHandler<{ amount: number; state?: string }> = data => {
        onSubmit(data);
        setHasBeenSubmitted(true);
        setIsAmountConfirmed(true);
        // Reset dirty state while keeping current values
        methods.reset(methods.getValues(), { keepValues: true });
    };

    const { isDirty } = methods.formState;
    const currentAmount = methods.watch('amount') ?? defaultAmount;

    const amountValidation = useLoanAmountValidation(currentAmount);
    const isAmountValid = amountValidation.isValidLoanAmount;

    // Disable plan selection when form becomes dirty after submission
    useEffect(() => {
        if (hasBeenSubmitted && isDirty) {
            setIsAmountConfirmed(false);
        }
    }, [hasBeenSubmitted, isDirty, setIsAmountConfirmed]);

    // Button is disabled if:
    // 1. Form has been submitted and is not dirty (already confirmed), OR
    // 2. Amount is invalid (below minimum or above maximum)
    const isSubmitDisabled = (hasBeenSubmitted && !isDirty) || !isAmountValid;

    if (isGoodLeap) {
        return (
            <GoodLeapForm
                methods={methods}
                handleSubmit={handleSubmit}
                resetAmount={resetAmount}
                isSubmitDisabled={isSubmitDisabled}
                defaultAmount={defaultAmount}
                defaultState={defaultState}
                availableStates={availableStates}
            />
        );
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <Flex direction="column" gap="6">
                    <Flex direction="column">
                        <Text variant="headline" el="h2" style={{ margin: 0 }}>
                            You're pre-qualified for up to {formatCurrency(prequalAmount)}
                        </Text>
                        <Text>Select an approved plan to continue your application.</Text>
                    </Flex>
                    <TextField
                        label="Desired Financed Amount"
                        name="amount"
                        type="number"
                        prefix="$"
                        inputMode="decimal"
                        required
                    />
                    <Flex direction="row" gap="2">
                        <Button appearance="primary" type="submit" disabled={isSubmitDisabled}>
                            Confirm Amount
                        </Button>
                        <Button
                            appearance="secondary"
                            onClick={resetAmount}
                            disabled={currentAmount === prequalAmount}
                        >
                            Cancel
                        </Button>
                    </Flex>
                </Flex>
            </form>
        </FormProvider>
    );
};
